import React from 'react';
import {
  HomeOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  LineChartOutlined,
  BarChartOutlined,
  PartitionOutlined,
  MailOutlined,
  BookOutlined,
  ReadOutlined,
  TagOutlined,
  FormOutlined,
  UsergroupAddOutlined,
  ShoppingOutlined,
  DollarOutlined,
  QrcodeOutlined,
  DesktopOutlined,
  CoffeeOutlined,
} from '@ant-design/icons';

import Home from 'pages/dashboard/Home/Home';
import ProductTable from 'pages/dashboard/ProductTable/ProductTable';
import ProductEditor from 'pages/dashboard/ProductEditor/ProductEditor';
// Legacy product planner from 2022, no longer in use — hidden from UI.
// import BriefEditor from 'pages/dashboard/BriefEditor/BriefEditor';
import StockAvailability from 'pages/dashboard/StockAvailability/StockAvailability';
import Reports from 'pages/dashboard/Reports/Reports';
import MailchimpEmails from 'pages/dashboard/Mailchimp/MailchimpEmails';
import MailchimpPathRedirects from 'pages/dashboard/Mailchimp/MailchimpPathRedirects';
import BeerBible from 'pages/dashboard/BeerBible/BeerBible';
import TapBadges from 'pages/dashboard/TapBadges/TapBadges';
import Foodstuffs from 'pages/dashboard/Foodstuffs/Foodstuffs';
import LabelBuilder from 'pages/dashboard/LabelBuilder/LabelBuilder';
import UserManagement from 'pages/dashboard/UserManagement/UserManagement';
import Webstore from 'pages/dashboard/Webstore/Webstore';
import QRCodes from 'pages/dashboard/QRCodes/QRCodes';
// import BriefTable from 'pages/dashboard/BriefTable/BriefTable';
import DTCProductBuilder from 'pages/dashboard/DTCProductBuilder/DTCProductBuilder';
import MondayIntegration from 'pages/dashboard/MondayIntegration';
import PricingManager from 'pages/dashboard/PricingManager/PricingManager';
import PricingDiscrepancies from 'pages/dashboard/PricingManager/PricingDiscrepancies/PricingDiscrepancies';
import PricingGuide from 'pages/dashboard/PricingManager/PricingGuide/PricingGuide';
import Segmentation from 'pages/dashboard/Segmentation/Segmentation';
import Forecasting from 'pages/dashboard/Forecasting/Forecasting';
import ComingSoon from 'pages/ComingSoon/ComingSoon';
import LandingPageSimulator from 'pages/dashboard/LandingPageSimulator/LandingPageSimulator';
import Usage from 'pages/dashboard/Usage/Usage';

/** Outlined seedling — Ant Design 4 has no plant icon. JSX not allowed in this .js file. */
const PlantOutlined = (props) => React.createElement(
  'span',
  {
    role: 'img', 'aria-label': 'plant', className: 'anticon anticon-plant', ...props,
  },
  React.createElement(
    'svg',
    {
      viewBox: '0 0 24 24',
      width: '1em',
      height: '1em',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: '1.75',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    React.createElement('path', { d: 'M12 21V10' }),
    React.createElement('path', { d: 'M12 14c-4 0-6-3-6-6 4 0 6 3 6 6z' }),
    React.createElement('path', { d: 'M12 12c4 0 6-3 6-6-4 0-6 3-6 6z' }),
  ),
);

/** Official Shopify bag mark. Ant Design has no Shopify icon. JSX not allowed here. */
const SHOPIFY_BAG_PATH = [
  'M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192',
  '-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075',
  '-.057-.121-.074l-.914 21.104h.023zM11.71 11.305s-.81-.424-1.774-.424c-1.447',
  ' 0-1.504.906-1.504 1.141 0 1.232 3.24 1.715 3.24 4.629 0 2.295-1.44 3.76',
  '-3.406 3.76-2.354 0-3.54-1.465-3.54-1.465l.646-2.086s1.245 1.066 2.28 1.066',
  'c.675 0 .975-.545.975-.932 0-1.619-2.654-1.694-2.654-4.359-.034-2.237 1.571',
  '-4.416 4.827-4.416 1.257 0 1.875.361 1.875.361l-.945 2.715-.02.01zM11.17.83',
  'c.136 0 .271.038.405.135-.984.465-2.064 1.639-2.508 3.992-.656.213-1.293.405',
  '-1.889.578C7.697 3.75 8.951.84 11.17.84V.83zm1.235 2.949v.135c-.754.232',
  '-1.583.484-2.394.736.466-1.777 1.333-2.645 2.085-2.971.193.501.309 1.176',
  '.309 2.1zm.539-2.234c.694.074 1.141.867 1.429 1.755-.349.114-.735.231-1.158',
  '.366v-.252c0-.752-.096-1.371-.271-1.871v.002zm2.992 1.289c-.02 0-.06.021',
  '-.078.021s-.289.075-.714.21c-.423-1.233-1.176-2.37-2.508-2.37h-.115C12.135',
  '.209 11.669 0 11.265 0 8.159 0 6.675 3.877 6.21 5.846c-1.194.365-2.063.636',
  '-2.16.674-.675.213-.694.232-.772.87-.075.462-1.83 14.063-1.83 14.063L15.009',
  ' 24l.927-21.166z',
].join('');

const ShopifyOutlined = (props) => React.createElement(
  'span',
  {
    role: 'img', 'aria-label': 'shopify', className: 'anticon anticon-shopify', ...props,
  },
  React.createElement(
    'svg',
    {
      viewBox: '0 0 24 24',
      width: '1em',
      height: '1em',
      fill: 'currentColor',
    },
    React.createElement('path', { d: SHOPIFY_BAG_PATH }),
  ),
);

/** Ordered sidebar section headers. `label: null` = no ItemGroup wrapper. */
export const MENU_SECTIONS = [
  { id: null, label: null },
  { id: 'product-data', label: 'Beer' },
  { id: 'production', label: 'Production' },
  { id: 'trade-sales', label: 'Sales' },
  { id: 'dtc', label: 'Ecommerce' },
  { id: 'create', label: 'Create' },
  { id: 'admin', label: 'Admin' },
];

export default [
  // --- (ungrouped) ---
  {
    menuLabel: 'Home',
    id: 'home',
    section: null,
    icon: HomeOutlined,
    description: 'Welcome to the Matrix',
    Component: Home,
    allowExternal: true,
  },

  // --- Product Data ---
  {
    menuLabel: 'Product Database',
    id: 'product-database',
    section: 'product-data',
    icon: DatabaseOutlined,
    allowExternal: true,
    subItems: [
      {
        id: 'products',
        menuLabel: 'Products',
        description: 'Manage products that have developed from a brief',
        Component: ProductTable,
        subItems: [
          {
            id: 'add',
            menuLabel: 'Add Product',
            description: 'Add a new product',
            hidden: true,
            backPath: '/dashboard/product-database/products',
            Component: ProductEditor,
          },
          {
            id: 'edit',
            menuLabel: 'Edit Product',
            description: 'Edit an existing product',
            hidden: true,
            backPath: '/dashboard/product-database/products',
            pathParam: 'productId',
            Component: ProductEditor,
          },
          {
            id: 'view',
            menuLabel: 'View Product',
            description: 'This is a read-only view of the product\'s details',
            hidden: true,
            backPath: '/dashboard/product-database/products',
            pathParam: 'productId',
            Component: ProductEditor,
            allowExternal: true,
          },
        ],
      },
      // ----- Product Briefs (legacy) -----
      // This was a product planner used in 2022 and is no longer needed.
      // The page components (BriefTable, BriefEditor) still exist in the
      // codebase but are unreachable. Backend routes and DB tables remain
      // intact in case historical data is ever needed.
      // {
      //   id: 'briefs',
      //   menuLabel: 'Product Briefs',
      //   description: 'Plan and develop future products',
      //   Component: BriefTable,
      //   permission: 'product-briefs',
      //   emailWhitelist: [ ... ],
      //   subItems: [
      //     { id: 'create', Component: BriefEditor, ... },
      //     { id: 'edit',   Component: BriefEditor, ... },
      //   ],
      // },
      {
        id: 'monday-integration',
        menuLabel: 'Product Syncing',
        description: 'Sync products with Monday, manage Cin7 / Kounta / Shopify caches, accept platform SKU links, and review connection and webhook status. Cin7 sync is rate-limited (60 req/min).',
        Component: MondayIntegration,
        permission: ['monday-integration', 'product-cache'],
        tabQueryLabel: 'tab',
        tabs: [
          {
            label: 'Product Sync Status',
            id: 'product-sync-statuses',
          },
          {
            label: 'SKU Sync Status',
            id: 'sku-sync-statuses',
          },
          {
            label: 'Cache',
            id: 'product-cache',
          },
          {
            label: 'Platform Links',
            id: 'platform-links',
            aliases: ['shopify-links'],
          },
          {
            label: 'Connection Status',
            id: 'connection-status',
          },
          {
            label: 'Webhook Statuses',
            id: 'webhook-statuses',
          },
          {
            label: 'SKU Migration',
            id: 'sku-migration',
          },
          {
            label: 'SAP Import',
            id: 'sap-import',
          },
        ],
      },
    ],
  },
  {
    menuLabel: 'Pricing',
    id: 'pricing',
    section: 'product-data',
    icon: DollarOutlined,
    permission: 'pricing.manager',
    subItems: [
      {
        menuLabel: 'Pricing Guide',
        id: 'guide',
        description: 'Edit target margins and pricing rules',
        permission: 'pricing.manager',
        Component: PricingGuide,
      },
      {
        menuLabel: 'Pricing Proposals',
        id: 'proposals',
        description: 'Create pricing proposals, review changes, and push updates to Monday, Cin7, and Kounta',
        permission: 'pricing.manager',
        Component: PricingManager,
      },
      {
        menuLabel: 'Pricing Discrepancies',
        id: 'discrepancies',
        description: 'Compare prices against the Pricing Guide across Matrix, Monday, Cin7, Shopify and Kounta',
        permission: 'pricing.manager',
        Component: PricingDiscrepancies,
      },
    ],
  },
  {
    menuLabel: 'Beer Bible',
    id: 'beer-bible',
    section: 'product-data',
    icon: BookOutlined,
    description: 'Learn about the stories and details behind Garage Project creations',
    Component: BeerBible,
    allowExternal: true,
  },

  // --- Production ---
  {
    menuLabel: 'Forecasting',
    id: 'forecasting',
    section: 'production',
    icon: LineChartOutlined,
    description: 'Core range demand forecast, projected stock and brew recommendations',
    Component: Forecasting,
    permission: 'forecasting',
    tabQueryLabel: 'tab',
    tabs: [
      { label: 'Brew plan', id: 'plan' },
      { label: 'Production schedule', id: 'schedule' },
      { label: 'Rules & targets', id: 'rules' },
      { label: 'Events', id: 'events' },
      { label: 'Accuracy', id: 'accuracy' },
    ],
  },
  {
    menuLabel: 'Label Builder',
    id: 'label-builder',
    section: 'production',
    icon: FormOutlined,
    Component: LabelBuilder,
    hasNestedSwitch: true,
    allowExternal: true,
    subItems: [
      {
        menuLabel: 'Label Builder',
        title: 'Label Builder',
        id: 'new',
        description: 'Pick a beer and SKU — label type is chosen automatically from the SKU',
        allowExternal: true,
      },
      {
        menuLabel: 'Slab',
        title: 'Slab label builder',
        id: 'slab',
        description: 'Generate slab labels ready for printing',
        allowExternal: true,
        legacy: true,
      },
      {
        menuLabel: 'Case',
        title: 'Case label builder',
        id: 'case',
        description: 'Generate case labels ready for printing',
        allowExternal: true,
        legacy: true,
      },
      {
        menuLabel: 'Keg',
        title: 'Keg label builder',
        id: 'keg',
        description: 'Generate keg labels ready for printing',
        allowExternal: true,
        legacy: true,
      },
      {
        menuLabel: 'Pallet',
        title: 'Pallet label builder',
        id: 'pallet',
        description: 'Generate pallet labels ready for printing',
        allowExternal: true,
        legacy: true,
      },
    ],
  },

  // --- Trade Sales ---
  {
    menuLabel: 'Stock Availability',
    id: 'stock',
    section: 'trade-sales',
    icon: EnvironmentOutlined,
    Component: StockAvailability,
    hasNestedSwitch: true,
    subItems: [
      {
        menuLabel: 'Distribution - NZ',
        id: 'distribution',
        description: 'Check out the stock we have available at Distribution',
      },
      {
        menuLabel: 'Scott Base - NZ',
        id: 'scott-base',
        description: 'Check out the stock we have available at Scott Base for Venues and Local Biz',
      },
      {
        menuLabel: 'Cellaring Stock - NZ',
        id: 'cellaring-stock',
        description: 'Check out the cellaring stock we have available',
      },
      {
        menuLabel: 'Motus - AU',
        id: 'motus',
        description: 'Check out the stock we have available at Motus',
      },
      {
        menuLabel: 'Peter Sadler - AU',
        id: 'peter-sadler',
        path: '/stock/peter-sadler',
        description: 'Check out the stock we have available at Peter Sadler',
      },
      {
        menuLabel: 'Growler Depot - AU',
        id: 'growler-depot',
        description: 'Check out the stock we have available at Growler Depot',
      },
    ],
  },
  {
    menuLabel: 'Segmentation',
    id: 'segmentation',
    section: 'trade-sales',
    icon: PartitionOutlined,
    description: 'Manage customer and audience segmentation',
    Component: Segmentation,
    permission: ['segmentation', 'segmentation.view'],
  },
  {
    menuLabel: 'Stock List Emails',
    id: 'emails',
    section: 'trade-sales',
    icon: MailOutlined,
    description: 'Stock list emails — legacy or SKU-based generator',
    Component: MailchimpEmails,
    hasNestedSwitch: true,
    subItems: [
      {
        menuLabel: 'Stock List Emails',
        title: 'Stock List Emails',
        id: 'new',
        description: '',
      },
      {
        menuLabel: 'Old Mailchimp',
        title: 'Legacy stock update email',
        id: 'old',
        description: 'Original builder using product pack/keg columns',
        legacy: true,
      },
    ],
  },
  {
    menuLabel: 'Foodstuffs',
    id: 'foodstuffs',
    section: 'trade-sales',
    icon: ReadOutlined,
    hidden: true,
    Component: Foodstuffs,
    hasNestedSwitch: true,
    subItems: [
      {
        menuLabel: 'Both Islands',
        title: 'Foodstuffs New Zealand',
        id: 'both-islands',
      },
      {
        menuLabel: 'North Island',
        title: 'Foodstuffs (North Island)',
        id: 'north-island',
      },
      {
        menuLabel: 'South Island',
        title: 'Foodstuffs (South Island)',
        id: 'south-island',
      },
    ],
  },
  {
    hidden: true,
    id: 'mailchimp',
    section: 'trade-sales',
    Component: MailchimpPathRedirects,
    hasNestedSwitch: true,
    subItems: [],
  },

  // --- DTC / Ecommerce ---
  {
    menuLabel: 'Webstore',
    id: 'webstore',
    section: 'dtc',
    icon: ShoppingOutlined,
    Component: Webstore,
    hasNestedSwitch: true,
    subItems: [
      {
        id: 'request',
        menuLabel: 'Request products',
        description: 'Request products from webstore inventory',
      },
      {
        id: 'review-requests',
        menuLabel: 'Review requests',
        description: 'Approve or reject webstore product requests',
        permission: 'webstore.request-products',
        tabQueryLabel: 'status',
        tabs: [
          {
            label: 'Pending',
            id: 'pending',
          },
          {
            label: 'Approved',
            id: 'approved',
          },
          {
            label: 'Rejected',
            id: 'rejected',
          },
          {
            label: 'Failed',
            id: 'failed',
          },
        ],
        emailWhitelist: [
          'dev@garageproject.co.nz',
          'gina@garageproject.co.nz',
          'sally@garageproject.co.nz',
          'leyton@garageproject.co.nz',
          'tyler.h@garageproject.co.nz',
          'kyle@garageproject.co.nz',
          'elliot@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'websales@garageproject.com.au',
          'adam.holliday@garageproject.co.nz',
          'adam@garageproject.co.nz',
          'jamie@garageproject.co.nz',
        ],
      },
      {
        id: 'shipping-rates',
        menuLabel: 'Shipping rates',
        description: 'Manage custom shipping rates',
        permission: 'webstore.shipping-rates',
        tabQueryLabel: 'shipFromCountry',
        tabs: [
          {
            label: 'New Zealand Webstores',
            id: 'NZ',
          },
          {
            label: 'Australia Webstores',
            id: 'AU',
          },
        ],
        emailWhitelist: [
          'dev@garageproject.co.nz',
          'leyton@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'kyle@garageproject.co.nz',
          'tyler.h@garageproject.co.nz',
          'elliot@garageproject.co.nz',
          'george@garageproject.co.nz',
          'gina@garageproject.co.nz',
          'sally@garageproject.co.nz',
          'janna@garageproject.co.nz',
          'matt.cameron@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'websales@garageproject.com.au',
          'jamie@garageproject.co.nz',
        ],
      },
      {
        id: 'builds',
        menuLabel: 'Website Builds',
        description: 'Trigger manual rebuilds of the Garage Project websites',
        permission: 'webstore.builds',
        emailWhitelist: [
          'dev@garageproject.co.nz',
          'leyton@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'kyle@garageproject.co.nz',
          'sally@garageproject.co.nz',
          'tyler.h@garageproject.co.nz',
          'elliot@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'websales@garageproject.com.au',
          'jamie@garageproject.co.nz',
        ],
      },
      {
        id: 'order-merch',
        menuLabel: 'Venues Merch Ordering',
        description: 'Merch stock numbers for Venues team',
        permission: 'webstore.order-merch',
        emailWhitelist: [
          'dev@garageproject.co.nz',
          'leyton@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'kyle@garageproject.co.nz',
          'tyler.h@garageproject.co.nz',
          'elliot@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'websales@garageproject.com.au',
          'lily@garageproject.co.nz',
          'ryan@garageproject.co.nz',
          'sam@garageproject.co.nz',
          'soph@garageproject.co.nz',
          'cellardoor@garageproject.co.nz',
          'lucy@garageproject.co.nz',
          'kingsland@garageproject.co.nz',
          'jamie@garageproject.co.nz',
        ],
      },
      // Still routed via Webstore.jsx; drawn under FRESH Club / Wild Club menu-only entries
      {
        id: 'fresh-club',
        menuLabel: 'FRESH Club Insights',
        description: 'Manage upcoming FRESH Club sales',
        permission: 'webstore.fresh-club',
        menuHidden: true,
        emailWhitelist: [
          'dev@garageproject.co.nz',
          'leyton@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'kyle@garageproject.co.nz',
          'tyler@garageproject.co.nz',
          'tyler.h@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'websales@garageproject.com.au',
          'peter.fenton@garageproject.co.nz',
          'jamie@garageproject.co.nz',
        ],
      },
      {
        id: 'fresh-club-onetimes',
        menuLabel: 'FRESH Club Onetimes',
        description: 'Add onetimes to Customer Subscriptions',
        permission: 'webstore.fresh-club-onetimes',
        menuHidden: true,
        emailWhitelist: [
          'dev@garageproject.co.nz',
          'leyton@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'kyle@garageproject.co.nz',
          'tyler@garageproject.co.nz',
          'tyler.h@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'websales@garageproject.com.au',
          'peter.fenton@garageproject.co.nz',
          'jamie@garageproject.co.nz',
        ],
      },
      {
        id: 'fresh-club-date-roll',
        menuLabel: 'FRESH Club Dates',
        description: 'Bulk change upcoming FRESH Club invoice dates',
        permission: 'webstore.fresh-club-date-roll',
        menuHidden: true,
      },
      {
        id: 'wild-club',
        menuLabel: 'Wild Club Insights',
        description: 'Wild Club membership and remaining-box insights',
        permission: 'webstore.wild-club',
        menuHidden: true,
      },
      {
        id: 'wild-club-date-roll',
        menuLabel: 'Wild Club Dates',
        description: 'Bulk change queued Wild Club shipment order dates (not charge dates)',
        permission: 'webstore.wild-club-date-roll',
        menuHidden: true,
      },
    ],
  },
  {
    menuLabel: 'FRESH Club',
    id: 'fresh-club-menu',
    section: 'dtc',
    icon: CoffeeOutlined,
    menuOnly: true,
    subItems: [
      {
        menuLabel: 'Insights',
        id: 'insights',
        menuPath: '/webstore/fresh-club',
        description: 'Manage upcoming FRESH Club sales',
        permission: 'webstore.fresh-club',
        emailWhitelist: [
          'dev@garageproject.co.nz',
          'leyton@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'kyle@garageproject.co.nz',
          'tyler@garageproject.co.nz',
          'tyler.h@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'websales@garageproject.com.au',
          'peter.fenton@garageproject.co.nz',
          'jamie@garageproject.co.nz',
        ],
      },
      {
        menuLabel: 'Onetimes',
        id: 'onetimes',
        menuPath: '/webstore/fresh-club-onetimes',
        description: 'Add onetimes to Customer Subscriptions',
        permission: 'webstore.fresh-club-onetimes',
        emailWhitelist: [
          'dev@garageproject.co.nz',
          'leyton@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'kyle@garageproject.co.nz',
          'tyler@garageproject.co.nz',
          'tyler.h@garageproject.co.nz',
          'websales@garageproject.co.nz',
          'websales@garageproject.com.au',
          'peter.fenton@garageproject.co.nz',
          'jamie@garageproject.co.nz',
        ],
      },
      {
        menuLabel: 'Dates',
        id: 'dates',
        menuPath: '/webstore/fresh-club-date-roll',
        description: 'Bulk change upcoming FRESH Club invoice dates',
        permission: 'webstore.fresh-club-date-roll',
      },
    ],
  },
  {
    menuLabel: 'Wild Club',
    id: 'wild-club-menu',
    section: 'dtc',
    icon: PlantOutlined,
    menuOnly: true,
    subItems: [
      {
        menuLabel: 'Insights',
        id: 'insights',
        menuPath: '/webstore/wild-club',
        description: 'Wild Club membership and remaining-box insights',
        permission: 'webstore.wild-club',
      },
      {
        menuLabel: 'Dates',
        id: 'dates',
        menuPath: '/webstore/wild-club-date-roll',
        description: 'Bulk change queued Wild Club shipment order dates (not charge dates)',
        permission: 'webstore.wild-club-date-roll',
      },
    ],
  },
  {
    menuLabel: 'Shopify Product Builder',
    id: 'dtc-product-builder',
    section: 'dtc',
    icon: ShopifyOutlined,
    description: 'Generate Product Imports for Kounta and Shopify',
    Component: DTCProductBuilder,
    permission: 'dtc-product-builder',
    hasNestedSwitch: true,
    allowExternal: true,
    subItems: [
      {
        menuLabel: 'Shopify Product Builder',
        title: 'SKU-based Shopify product generator',
        id: 'new',
        description: 'Generate Shopify draft products from SKU barcodes',
        allowExternal: false,
      },
      {
        menuLabel: 'Legacy',
        title: 'Legacy Shopify product generator',
        id: 'shopify',
        description: 'Generate Shopify product import using legacy barcode columns',
        allowExternal: false,
        legacy: true,
      },
    ],
    emailWhitelist: [
      'dev@garageproject.co.nz',
      'kyle@garageproject.co.nz',
      'leyton@garageproject.co.nz',
      'tyler.h@garageproject.co.nz',
      'tyler@garageproject.co.nz',
      'jamie@garageproject.co.nz',
      'george@garageproject.co.nz',
      'gina@garageproject.co.nz',
    ],
  },

  // --- Create ---
  {
    menuLabel: 'Tap Badges',
    id: 'tap-badges',
    section: 'create',
    icon: TagOutlined,
    description: 'Generate tap badges ready for printing',
    Component: TapBadges,
    allowExternal: true,
  },
  {
    menuLabel: 'QR Codes',
    id: 'qr-codes',
    section: 'create',
    icon: QrcodeOutlined,
    description: 'Create and update QR codes without needing to reprint them',
    Component: QRCodes,
  },
  {
    menuLabel: 'Landing Page',
    id: 'landing-page',
    section: 'create',
    icon: DesktopOutlined,
    description: 'Preview landing page assets at real device sizes before publishing',
    Component: LandingPageSimulator,
    hideFooter: true,
  },
  {
    menuLabel: 'URL Shortener',
    id: 'url-shortener',
    section: 'create',
    hidden: true,
    description: 'Create and manage short URLs',
    Component: ComingSoon,
  },
  {
    menuLabel: 'Quick Renderer',
    id: 'quick-renderer',
    section: 'create',
    hidden: true,
    description: 'Quickly render product or campaign assets for web and social',
    Component: ComingSoon,
  },

  // --- Admin ---
  {
    menuLabel: 'Reports',
    id: 'reports',
    section: 'admin',
    icon: LineChartOutlined,
    description: 'View and manage reports',
    Component: Reports,
    permission: 'reports.view',
  },
  {
    menuLabel: 'Usage',
    id: 'usage',
    section: 'admin',
    icon: BarChartOutlined,
    description: 'Who uses the Matrix, which pages and features, and for how long',
    Component: Usage,
    permission: 'usage.view',
  },
  {
    menuLabel: 'User Management',
    requiresAdmin: true,
    permission: 'permission-admin',
    id: 'users',
    section: 'admin',
    icon: UsergroupAddOutlined,
    description: 'Manage details and permissions for staff and external user accounts',
    Component: UserManagement,
    tabQueryLabel: 'type',
    tabs: [
      {
        label: 'Staff',
        id: 'staff',
      },
      {
        label: 'External',
        id: 'external',
      },
      {
        label: 'Roles & routes',
        id: 'roles-routes',
      },
    ],
    emailWhitelist: [
      'dev@garageproject.co.nz',
      'leyton@garageproject.co.nz',
      'mattie@garageproject.co.nz',
      'laura@garageproject.co.nz',
      'kyle@garageproject.co.nz',
    ],
  },
];

/**
 * Flatten route config to a list of items that are gated by a permission (for Roles & routes mapping).
 * Includes permission-only entries for capabilities that aren't top-level menu items (e.g. reports.write, sku.view).
 */
export const PERMISSION_ONLY_ROUTES = [
  { menuLabel: 'Reports (create, edit, delete)', permission: 'reports.write', id: 'reports.write' },
  { menuLabel: 'Product Editor – SKU tab (view)', permission: 'sku.view', id: 'sku.view' },
  { menuLabel: 'Product Editor – SKU tab (edit)', permission: 'sku.edit', id: 'sku.edit' },
  { menuLabel: 'Product Editor – SKU-first layout (legacy tabs hidden)', permission: 'sku.new-layout', id: 'sku.new-layout' },
  { menuLabel: 'Product & SKU change history (view)', permission: 'activity.view', id: 'activity.view' },
];

function flattenPermissionRoutes(items, out = []) {
  if (!items || !Array.isArray(items)) return out;
  items.forEach((item) => {
    if (item.menuOnly) {
      // Menu-only entries mirror permissions already on real webstore routes — skip to avoid dupes
      return;
    }
    if (item.permission) {
      const rawLabel = item.menuLabel ?? item.title ?? item.id;
      const keys = Array.isArray(item.permission) ? item.permission : [item.permission];
      keys.forEach((permission) => {
        const base = rawLabel == null ? '' : String(rawLabel);
        const menuLabel = keys.length > 1 ? `${base} — ${permission}` : base;
        out.push({
          menuLabel,
          permission,
          id: `${item.id}__${permission}`,
        });
      });
    }
    flattenPermissionRoutes(item.subItems, out);
  });
  return out;
}

/**
 * @param {Array} routeConfig - the default route config array
 * @returns {{ menuLabel: string, permission: string, id: string }[]}
 */
export function getPermissionGatedRoutes(routeConfig) {
  const fromConfig = flattenPermissionRoutes(routeConfig || []);
  const byPermission = new Map(fromConfig.map((r) => [r.permission, r]));
  PERMISSION_ONLY_ROUTES.forEach((r) => {
    if (!byPermission.has(r.permission)) byPermission.set(r.permission, r);
  });
  return Array.from(byPermission.values()).sort((a, b) => (
    String(a.menuLabel ?? '').localeCompare(String(b.menuLabel ?? ''))
  ));
}
